import {
  VariationActionType,
  ClassifierType,
  LONG_DATE_FORMAT,
  VariationUnitType,
  RemunerationType,
} from '$constants'
import { formatCurrency, formatPercent } from 'src/components/common/helpers'
import { splitAndLowerCaseAndInsertSpaces } from '$utils'
import { Nullable } from '../types'
import { IPayClassificationVariationModel } from '@loadedhub/core-api-sdk'

export { PayClassificationVariationModel } from '@loadedhub/core-api-sdk'

export interface PayClassificationVariation
  extends IPayClassificationVariationModel {
  description?: Nullable<string>
}

// Function to apply transformations to Variation object
export const transformVariationOnGet = (
  variation: IPayClassificationVariationModel
): PayClassificationVariation => {
  return {
    ...variation,
    description: buildDescription(variation),
  } as PayClassificationVariation
}

// Function to get the value to display in description
const getValues = (variation: PayClassificationVariation) => {
  let value: string = ''
  let classifier: string = ''
  const action: string =
    variation.action === VariationActionType.Add
      ? 'the base pay plus'
      : 'the base pay minus'
  const logicalOperator: string = splitAndLowerCaseAndInsertSpaces(
    variation.logicalOperator.toString()
  )

  let actionValue: string = ''
  let unitType: string = ''
  if (variation.unitType === VariationUnitType.Currency) {
    actionValue = formatCurrency(variation.actionValue, true)
    unitType =
      variation.actionValue && variation.actionValue > 1 ? 'dollars' : 'dollar'
  } else {
    actionValue = formatPercent(variation.actionValue, true)
    unitType = 'percent'
  }

  switch (variation.classifier) {
    case ClassifierType.Employment:
      value = splitAndLowerCaseAndInsertSpaces(
        variation.employmentType!.toString()
      )
      classifier = 'employment type'
      break
    case ClassifierType.Age:
      value = `${variation.age}`
      classifier = 'age'
      break
    case ClassifierType.ContractedWeeklyHours:
      value = `${variation.contractedWeeklyHours}`
      classifier = 'contracted weekly hours'
      break
    case ClassifierType.Remuneration:
      value =
        variation.remunerationType === RemunerationType.Salary
          ? 'salary'
          : 'hourly rate'
      classifier = 'remuneration type'
      break
    case ClassifierType.StartDate:
      if (!variation.startDate) break
      value = variation.startDate.format(LONG_DATE_FORMAT)
      classifier = 'start date'
      break
  }

  return { value, classifier, action, logicalOperator, actionValue, unitType }
}

// Function to apply description transformations to Variation object
export const buildDescription = (
  variation: PayClassificationVariation
): string => {
  const { value, classifier, action, logicalOperator, actionValue, unitType } =
    getValues(variation)

  const description: string = `If the employees ${classifier} ${logicalOperator} ${value} then set the base hourly payrate to ${action} ${actionValue} ${unitType}`

  return description
}
